import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import {
  generatePath, RouteComponentProps, useHistory, withRouter,
} from 'react-router-dom';
import {
  isError,
  isFinishedCreating, isFinishedFetching, isNew, ResourcesItem, ResourcesList,
} from 'redux-and-the-rest';
import { IslandModel } from '../../models/islandModel';
import { IslandThemeModel } from '../../models/islandThemeModel';

import {
  getNewOrExistingIsland, createIsland, ISLAND_INCLUDES, clearIslandResource,
} from '../../redux/reducers/islandReducer';
import { getOrFetchIslandThemes } from '../../redux/reducers/islandThemeReducer';
import generateClientId from '../../redux/reducers/utils/generateClientId';
import { ApplicationState } from '../../redux/types';
import { Routes } from '../../routes';
import LoadingIndicator from '../shared/LoadingIndicator';

interface Props {
  island: ResourcesItem<IslandModel>;
  islandThemes: ResourcesList<IslandThemeModel>;
}

/**
 * Container component that creates a new island and redirects to the view component with the generated
 * id once it has been successfully created.
 */
const IslandContainerNew = ({ island, islandThemes }: Props): React.ReactElement => {
  const history = useHistory();

  useEffect(() => {
    /**
     * In order to create a new island, we must select a theme.
     * By default we choose the first one.
     */

    if (isNew(island) && isFinishedFetching(islandThemes)) {
      const { items: islandThemeItems } = islandThemes;
      const { values: defaultIslandTheme } = islandThemeItems[0];

      createIsland({
        include: ISLAND_INCLUDES,
      }, {
        id: generateClientId(),
        type: 'island',
        theme: defaultIslandTheme,
        relationshipNames: ['theme'],
      }, {
        push: [{}],
      });

      return;
    }

    /**
     * Once an island has been fetched/created, we change the path to the Island View one.
     */
    if (isFinishedCreating(island) && !isError(island)) {
      const { values: { id } } = island;

      /**
       * Before redirecting to the island, we must reset the island resource.
       * This is due to the getNewOrExisting in AddEditIsland returning this new island in the manage
       * island list, which makes AddEditIsland update this new island rather than add a new one.
       * See https://dubitlimited.atlassian.net/browse/AB-250
       */
      clearIslandResource();

      history.replace(generatePath(Routes.Islands.View, { id }));
    }
  }, [island, islandThemes]);

  return <LoadingIndicator />;
};

const mapStateToProps = (
  { islands, islandThemes }: ApplicationState,
  { match: { params: { id } } }: RouteComponentProps<{ id: string; }>,
) => ({
  island: getNewOrExistingIsland(islands, { id }),
  islandThemes: getOrFetchIslandThemes(islandThemes),
});

export default withRouter(connect(mapStateToProps)(IslandContainerNew));
